import React, { FC, useContext, useEffect, useState } from 'react'
import cn from 'classnames'
import { ISlide } from '../../../types'
import SwiperCore, { SwiperOptions, EffectFade } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import styles from './ReviewSlider.module.scss'
import ArrowIcon from '../../../../public/static/icons/arrow-review.svg'
import VideoSlide from './VideoSlide'
import ImgSlide from './ImgSlide'
import { ReviewDetailContext } from '../../../../contexts/ReviewDetailProvider'
import { SortOrder } from '../../../../__generated__/globalTypes'

interface IReviewSlider {
  className?: string
  slides: ISlide[]
  initialSlide: number
}

const ReviewSlider: FC<IReviewSlider> = ({ slides, initialSlide }) => {
  const { getAnotherReview, hasPrevReview, hasNextReview, id, handlerSlide } =
    useContext(ReviewDetailContext)
  const [swiper, setSwiper] = useState<SwiperCore>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [prevX, setPrevX] = useState(null)
  const [isFirstSlide, setIsFirstSlide] = useState(true)

  const handleTouchEnd = (swiper: SwiperCore) => {
    const deltaX = Math.abs(swiper.touches.currentX - prevX)
    const deltaY = Math.abs(swiper.touches.currentY - swiper.touches.startY)

    if (deltaY > deltaX) {
      return
    }

    if (swiper.realIndex === 0) {
      setIsFirstSlide(true)
    }

    if (
      isFirstSlide &&
      hasPrevReview &&
      prevX &&
      prevX < swiper.touches.currentX
    ) {
      getAnotherReview(SortOrder.asc)
    } else if (swiper.isEnd && hasNextReview) {
      if (prevX && prevX > swiper.touches.currentX) {
        getAnotherReview(SortOrder.desc)
      }
    }
  }

  const options: SwiperOptions = {
    effect: 'fade',
    allowTouchMove: false,
    modules: [EffectFade],
    breakpoints: {
      992: {
        allowTouchMove: false,
      },
      300: {
        allowTouchMove: true,
      },
    },
  }

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(0)
    }
  }, [id])

  return (
    <Swiper
      {...options}
      onInit={(s) => setSwiper(s)}
      onSlideChange={(swiper) => {
        const index = swiper.realIndex

        setActiveIndex(index)
        handlerSlide(slides[index].id)

        if (swiper.realIndex > 0) {
          setIsFirstSlide(false)
        }
      }}
      className={styles.slider}
      onTouchStart={() => setPrevX(swiper.touches.startX)}
      onTouchEnd={handleTouchEnd}
      initialSlide={initialSlide}
    >
      {slides.map((slide, index) => (
        <SwiperSlide key={slide.id} className={styles.slide}>
          {slide.type === 'img' ? (
            <ImgSlide
              src={slide.src}
              index={index}
              width={slide.width}
              height={slide.height}
            />
          ) : (
            <VideoSlide
              src={slide.src}
              cover={slide.cover}
              active={index === activeIndex}
              index={index}
            />
          )}
        </SwiperSlide>
      ))}
      <div className={styles.buttons}>
        <button
          type="button"
          title="Предыдущий слайд"
          className={cn(
            styles.button,
            styles['button--prev'],
            activeIndex === 0 && !hasPrevReview && styles['button--hidden'],
          )}
          onClick={() => {
            if (swiper?.activeIndex === 0) {
              getAnotherReview(SortOrder.asc)
            } else {
              swiper?.slidePrev()
            }
          }}
        >
          <ArrowIcon />
        </button>
        <button
          type="button"
          title="Следующий слайд"
          className={cn(
            styles.button,
            styles['button--next'],
            activeIndex === slides.length - 1 &&
              !hasNextReview &&
              styles['button--hidden'],
          )}
          onClick={() => {
            if (swiper?.activeIndex === slides.length - 1) {
              getAnotherReview(SortOrder.desc)
            } else {
              swiper?.slideNext()
            }
          }}
        >
          <ArrowIcon />
        </button>
      </div>
      <div className={styles.counter}>
        {activeIndex + 1}/{slides.length}
      </div>
    </Swiper>
  )
}

export default ReviewSlider
